/**
 * Shared UI primitives used across all movement forms.
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeftRight } from 'lucide-react';
import { useStockMovementStore } from '../../../store/stockMovementStore';
import { Item } from '../../../store/inventoryStore';

// ── Colour tokens (dark-mode only — all movement forms live in the dark theme context) ──

export const colours = {
  pageBg:   '#171a1f',
  cardBg:   '#20242b',
  border:   '#343a43',
  headerBg: '#20242b',
  inputBg:  '#171a1f',
  text:     '#e5e7eb',
  muted:    '#9ca3af',
  rowHover: '#262b35',
};

// ── Step indicator ────────────────────────────────────────────────────────

interface StepBarProps {
  steps: string[];
  current: number; // 0-based
}
export const StepBar: React.FC<StepBarProps> = ({ steps, current }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    width: '100%',
    padding: '10px 16px',
    borderBottom: `1px solid ${colours.border}`,
    boxSizing: 'border-box',
  }}>
    {steps.map((label, i) => {
      const done   = i < current;
      const active = i === current;
      return (
        <React.Fragment key={i}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 22,
              height: 22,
              borderRadius: '50%',
              fontSize: 12,
              fontWeight: 700,
              flexShrink: 0,
              backgroundColor: done ? '#16a34a' : active ? '#3b82f6' : '#374151',
              color: '#fff',
            }}>
              {done ? '✓' : i + 1}
            </span>
            <span style={{
              fontSize: 13,
              fontWeight: 500,
              whiteSpace: 'nowrap',
              color: active ? colours.text : done ? '#6b7280' : '#4b5563',
            }}>
              {label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div style={{
              flex: 1,
              height: 1,
              margin: '0 16px',
              backgroundColor: done ? '#16a34a' : colours.border,
            }} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ── Section card ─────────────────────────────────────────────────────────

export const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div
    className={`rounded overflow-hidden ${className}`}
    style={{ backgroundColor: colours.cardBg, border: `1px solid ${colours.border}` }}
  >
    {children}
  </div>
);

// ── Field label ──────────────────────────────────────────────────────────

export const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="text-xs uppercase tracking-wide font-medium" style={{ color: colours.muted }}>
    {children}
  </span>
);

// ── Owners ───────────────────────────────────────────────────────────────

export function sortUniqueOwners(values: (string | null | undefined)[]): string[] {
  const set = new Set<string>();
  for (const v of values) {
    const trimmed = v?.trim();
    if (trimmed) set.add(trimmed);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

// ── Select ───────────────────────────────────────────────────────────────

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  wFull?: boolean;
}
export const Select: React.FC<SelectProps> = ({ label, wFull, children, value, ...rest }) => (
  <div className={`flex flex-col gap-1 ${wFull ? 'w-full' : ''}`}>
    {label && <FieldLabel>{label}</FieldLabel>}
    <select
      className="w-full text-sm px-2 py-1.5 rounded border focus:outline-none focus:ring-1 focus:ring-blue-500"
      style={{
        backgroundColor: colours.inputBg,
        borderColor: colours.border,
        color: value ? colours.text : colours.muted,
      }}
      value={value}
      {...rest}
    >
      {children}
    </select>
  </div>
);

// ── Typeable item picker ─────────────────────────────────────────────────

interface ItemComboboxProps {
  value: string;
  items: Item[];
  onChange: (itemId: string) => void;
  placeholder?: string;
  compact?: boolean;
}

function itemMatchesQuery(item: Item, q: string): boolean {
  if (!q) return true;
  const name = item.name.toLowerCase();
  const sku = item.sku?.toLowerCase() ?? '';
  return name.includes(q) || sku.includes(q);
}

function rankItemMatch(item: Item, q: string): number {
  if (!q) return 0;
  const name = item.name.toLowerCase();
  if (name === q) return 0;
  if (name.startsWith(q)) return 1;
  if (item.sku?.toLowerCase() === q) return 2;
  return 3;
}

export const ItemCombobox: React.FC<ItemComboboxProps> = ({
  value,
  items,
  onChange,
  placeholder = 'Type to find item…',
  compact = false,
}) => {
  const selected = items.find(i => i.id === value) ?? null;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(selected?.name ?? '');
  const [filterActive, setFilterActive] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [menuPos, setMenuPos] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const focusedRef = useRef(false);
  const pickingRef = useRef(false);

  useEffect(() => {
    if (!focusedRef.current) setQuery(selected?.name ?? '');
  }, [selected?.name, value]);

  const filtered = useMemo(() => {
    const q = filterActive ? query.trim().toLowerCase() : '';
    return [...items]
      .filter(i => itemMatchesQuery(i, q))
      .sort((a, b) => rankItemMatch(a, q) - rankItemMatch(b, q) || a.name.localeCompare(b.name));
  }, [items, query, filterActive]);

  useEffect(() => {
    if (!open) return;
    if (!filterActive && value) {
      const idx = filtered.findIndex(i => i.id === value);
      setHighlight(idx >= 0 ? idx : 0);
    } else {
      setHighlight(0);
    }
  }, [query, open, filterActive, value, filtered]);

  const updateMenuPos = () => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 4;
    const menuMaxH = 260;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const openUp = spaceBelow < 160 && spaceAbove > spaceBelow;
    const maxHeight = Math.max(120, Math.min(menuMaxH, openUp ? spaceAbove - 8 : spaceBelow - 8));
    const top = openUp ? Math.max(8, rect.top - gap - maxHeight) : rect.bottom + gap;
    let left = rect.left;
    const width = Math.max(rect.width, 220);
    if (left + width > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - width - 8);
    }
    setMenuPos({ top, left, width, maxHeight });
  };

  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    updateMenuPos();
    window.addEventListener('scroll', updateMenuPos, true);
    window.addEventListener('resize', updateMenuPos);
    return () => {
      window.removeEventListener('scroll', updateMenuPos, true);
      window.removeEventListener('resize', updateMenuPos);
    };
  }, [open, filtered.length]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
      focusedRef.current = false;
      setQuery(selected?.name ?? '');
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, selected?.name]);

  useEffect(() => {
    if (!open) return;
    const el = menuRef.current?.querySelector(`[data-combo-idx="${highlight}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlight, open, menuPos]);

  const pick = (item: Item) => {
    pickingRef.current = true;
    onChange(item.id);
    setQuery(item.name);
    setFilterActive(false);
    setOpen(false);
    focusedRef.current = false;
    inputRef.current?.blur();
  };

  const commitOrRevert = () => {
    const q = query.trim().toLowerCase();
    if (!q) {
      onChange('');
      setQuery('');
      return;
    }
    const exact = filtered.find(i => i.name.toLowerCase() === q)
      ?? filtered.find(i => i.sku?.toLowerCase() === q);
    if (exact) {
      onChange(exact.id);
      setQuery(exact.name);
      return;
    }
    if (filtered.length === 1) {
      pick(filtered[0]);
      return;
    }
    setQuery(selected?.name ?? '');
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlight(h => Math.min(h + 1, Math.max(0, filtered.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && filtered[highlight]) pick(filtered[highlight]);
      else commitOrRevert();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      setQuery(selected?.name ?? '');
      inputRef.current?.blur();
    }
  };

  const menu = open && menuPos
    ? createPortal(
        <div
          ref={menuRef}
          className="fixed z-[70] rounded-md shadow-xl overflow-hidden flex flex-col"
          style={{
            top: menuPos.top,
            left: menuPos.left,
            width: menuPos.width,
            maxHeight: menuPos.maxHeight,
            backgroundColor: colours.cardBg,
            border: `1px solid ${colours.border}`,
          }}
        >
          <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
            {filtered.length === 0 ? (
              <div className="px-2.5 py-3 text-xs text-center" style={{ color: colours.muted }}>
                No matching items
              </div>
            ) : (
              filtered.map((item, i) => {
                const active = i === highlight;
                const isSelected = item.id === value;
                return (
                  <button
                    key={item.id}
                    type="button"
                    data-combo-idx={i}
                    onMouseEnter={() => setHighlight(i)}
                    onMouseDown={e => {
                      e.preventDefault();
                      pick(item);
                    }}
                    className={`w-full text-left transition-colors ${
                      compact ? 'px-2 py-1 text-xs' : 'px-2.5 py-1.5 text-sm'
                    }`}
                    style={{
                      color: isSelected ? '#93c5fd' : colours.text,
                      backgroundColor: active ? colours.rowHover : 'transparent',
                    }}
                  >
                    <span className="block truncate">{item.name}</span>
                    {item.sku && (
                      <span className="block truncate text-xs" style={{ color: colours.muted }}>
                        {item.sku}
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <div ref={wrapRef} className="relative w-full">
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        placeholder={placeholder}
        value={query}
        onChange={e => {
          const next = e.target.value;
          setQuery(next);
          setFilterActive(true);
          setOpen(true);
          if (selected && next.trim().toLowerCase() !== selected.name.toLowerCase()) {
            onChange('');
          }
        }}
        onFocus={() => {
          focusedRef.current = true;
          setFilterActive(false);
          setOpen(true);
          inputRef.current?.select();
        }}
        onBlur={() => {
          if (pickingRef.current) {
            pickingRef.current = false;
            focusedRef.current = false;
            setFilterActive(false);
            setOpen(false);
            return;
          }
          focusedRef.current = false;
          setFilterActive(false);
          setOpen(false);
          commitOrRevert();
        }}
        onKeyDown={onKeyDown}
        className={`w-full rounded border focus:outline-none focus:ring-1 focus:ring-blue-500 ${
          compact ? 'text-xs px-2 py-1' : 'text-sm px-2 py-1.5'
        }`}
        style={{
          backgroundColor: colours.inputBg,
          borderColor: colours.border,
          color: query ? colours.text : colours.muted,
        }}
      />
      {menu}
    </div>
  );
};

// ── Input ────────────────────────────────────────────────────────────────

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  wFull?: boolean;
}
export const Input: React.FC<InputProps> = ({ label, wFull, ...rest }) => (
  <div className={`flex flex-col gap-1 ${wFull ? 'w-full' : ''}`}>
    {label && <FieldLabel>{label}</FieldLabel>}
    <input
      className="text-sm px-2 py-1.5 rounded border focus:outline-none focus:ring-1 focus:ring-blue-500"
      style={{ backgroundColor: colours.inputBg, borderColor: colours.border, color: colours.text }}
      {...rest}
    />
  </div>
);

// ── Qty input (native spin buttons need color-scheme for dark/light) ─────────

interface QtyInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  isDark?: boolean;
  compact?: boolean;
}

export const QtyInput: React.FC<QtyInputProps> = ({ isDark = true, compact = false, className = '', style, ...rest }) => (
  <input
    type="number"
    min={1}
    className={`movement-qty-input rounded border focus:outline-none focus:ring-1 focus:ring-blue-500 text-center ${
      compact ? 'w-14 text-xs px-1.5 py-1' : 'w-16 text-sm px-2 py-1.5'
    } ${className}`}
    style={{
      backgroundColor: colours.inputBg,
      borderColor: colours.border,
      color: colours.text,
      colorScheme: isDark ? 'dark' : 'light',
      ...style,
    }}
    {...rest}
  />
);

// ── Nav buttons ───────────────────────────────────────────────────────────

interface NavProps {
  onBack?: () => void;
  onNext?: () => void;
  onQueue?: () => void;
  onReview?: () => void;
  nextLabel?: string;
  queueLabel?: string;
  nextDisabled?: boolean;
  queueDisabled?: boolean;
  isLastStep?: boolean;
}
export const StepNav: React.FC<NavProps> = ({
  onBack, onNext, onQueue, onReview,
  nextLabel = 'Next',
  queueLabel = 'Add to Queue',
  nextDisabled, queueDisabled,
}) => {
  const pendingCount = useStockMovementStore(s => s.pendingActions.length);

  const actionBtnBase =
    'inline-flex items-center justify-center text-sm px-4 py-1.5 rounded font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed';

  return (
  <div
    className="flex items-center justify-between px-4 py-1.5 flex-shrink-0"
    style={{ borderTop: `1px solid ${colours.border}`, backgroundColor: colours.cardBg }}
  >
    <div>
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="text-sm px-3 py-1.5 rounded border transition-colors"
          style={{ borderColor: colours.border, color: colours.muted }}
          onMouseEnter={e => (e.currentTarget.style.backgroundColor = colours.rowHover)}
          onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
        >
          ← Back
        </button>
      )}
    </div>
    <div className="flex items-center gap-2">
      {onReview && (
        <button
          type="button"
          onClick={onReview}
          disabled={pendingCount === 0}
          className={`${actionBtnBase} gap-1.5 min-w-[11.75rem]`}
          style={{ backgroundColor: '#3b82f6', color: '#fff' }}
        >
          <ArrowLeftRight size={14} className="flex-shrink-0" />
          <span>Review &amp; Commit</span>
          <span
            className={`inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-white/20 text-[10px] font-bold leading-none ${
              pendingCount === 0 ? 'invisible' : ''
            }`}
          >
            {pendingCount || 0}
          </span>
        </button>
      )}
      {/* Fixed-width slot so Next ↔ Add to Queue does not resize the footer */}
      {(onNext || onQueue) && (
        <div className="inline-flex min-w-[9.75rem]">
          {onNext && (
            <button
              type="button"
              onClick={onNext}
              disabled={nextDisabled}
              className={`${actionBtnBase} w-full`}
              style={{ backgroundColor: '#3b82f6', color: '#fff' }}
            >
              {nextLabel} →
            </button>
          )}
          {onQueue && (
            <button
              type="button"
              onClick={onQueue}
              disabled={queueDisabled}
              className={`${actionBtnBase} w-full`}
              style={{ backgroundColor: '#16a34a', color: '#fff' }}
            >
              {queueLabel}
            </button>
          )}
        </div>
      )}
    </div>
  </div>
  );
};

// ── Condition badge (matches Inventory table) ─────────────────────────────

export const conditionBadgeClass = (condition: string | null | undefined, isDark: boolean): string => {
  // Blue + slate — kept separate from status colours (green / amber / red)
  if (condition === 'new')  return isDark ? 'bg-blue-900/40 text-blue-400'   : 'bg-blue-100 text-blue-800';
  if (condition === 'used') return isDark ? 'bg-zinc-700/70 text-zinc-300'   : 'bg-zinc-200 text-zinc-700';
  return isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-400';
};

export const ConditionBadge: React.FC<{ condition: string | null | undefined; isDark?: boolean }> = ({
  condition,
  isDark = true,
}) => (
  <span
    className={`inline-flex min-w-[3.25rem] justify-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${conditionBadgeClass(condition, isDark)}`}
  >
    {condition ?? '—'}
  </span>
);

// ── Empty state ────────────────────────────────────────────────────────────

export const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex-1 flex items-center justify-center">
    <p className="text-sm" style={{ color: colours.muted }}>{message}</p>
  </div>
);
